import type {
  MessageResponse,
  ToolResult,
  ToolResultStatus,
} from '@saaga/shared/interfaces/message.interface'
import type {
  ChatParticipant,
  ToolCallEntity,
  ToolStatus,
} from '@saaga/shared/store/slices/chat-message-entity.interface'

const STATUS_MAPPING: Record<ToolResultStatus, ToolStatus> = {
  running: 'running',
  completed: 'completed',
  error: 'error',
  awaiting_user: 'pending',
  answered: 'completed',
}

const resolveToolStatus = (entry: ToolResult): ToolStatus => {
  if (entry.is_error) return 'error'
  if (entry.status === 'completed' && entry.is_no_data === true) return 'no-data'
  if (entry.status) return STATUS_MAPPING[entry.status]
  return 'running'
}

const stableEntityId = (parentId: string, entry: ToolResult, index: number): string => {
  if (entry.tool_call_id) return `${parentId}::tc::${entry.tool_call_id}`
  return `${parentId}::tc::${index}`
}

const optionalString = (value: string | null | undefined): string | undefined =>
  value === null || value === undefined ? undefined : value

const optionalNumber = (value: number | null | undefined): number | undefined =>
  value === null || value === undefined ? undefined : value

interface BuildContext {
  parent: MessageResponse
  participant: ChatParticipant
  index: number
  entry: ToolResult
}

interface ParentMetadataFields {
  threadId: string | undefined
  parentThreadId: string | null
  turnStartedAt: string
  agentLabel: string | undefined
}

const extractParentMetadata = (parent: MessageResponse): ParentMetadataFields => ({
  threadId: parent.msg_metadata?.agent_execution_thread_id as string | undefined,
  parentThreadId: (parent.msg_metadata?.parent_thread_id as string | null | undefined) ?? null,
  turnStartedAt: (parent.msg_metadata?.turn_started_at as string | undefined) ?? parent.created_at,
  agentLabel: parent.msg_metadata?.agent_label as string | undefined,
})

interface ToolDisplayFields {
  toolName: string
  friendlyName: string | undefined
  toolStatus: ReturnType<typeof resolveToolStatus>
  toolInput: string | undefined
  toolOutput: string | undefined
  toolErrorMessage: string | undefined
  htmlResultKey: string | undefined
}

const buildToolDisplayFields = (entry: ToolResult): ToolDisplayFields => ({
  toolName: entry.tool_slug ?? entry.tool_label ?? 'unknown',
  friendlyName: entry.tool_label ?? entry.tool_slug ?? undefined,
  toolStatus: resolveToolStatus(entry),
  toolInput: entry.args ? JSON.stringify(entry.args, null, 2) : undefined,
  toolOutput: entry.result?.preview,
  toolErrorMessage: entry.is_error ? entry.result?.preview : undefined,
  htmlResultKey: optionalString(entry.html_result_key),
})

interface ToolStructuredFields {
  toolCallId: string | undefined
  toolSlug: string | undefined
  toolLabel: string | undefined
  toolCategory: string | undefined
  toolArgs: Record<string, unknown> | undefined
  toolResultData: Record<string, unknown> | null
  toolResultBlobRef: string | null
  toolStartedAt: string | undefined
  toolFinishedAt: string | null
}

const buildToolStructuredFields = (entry: ToolResult): ToolStructuredFields => ({
  toolCallId: entry.tool_call_id,
  toolSlug: entry.tool_slug,
  toolLabel: entry.tool_label,
  toolCategory: entry.category,
  toolArgs: entry.args,
  toolResultData: entry.result?.data ?? null,
  toolResultBlobRef: entry.result?.blob_ref ?? null,
  toolStartedAt: entry.started_at,
  toolFinishedAt: entry.finished_at ?? null,
})

const isStreamingStatus = (status: ToolResult['status']): boolean =>
  status === 'running' || status === 'awaiting_user'

const buildEntity = (ctx: BuildContext): ToolCallEntity => {
  const { parent, participant, index, entry } = ctx
  const meta = extractParentMetadata(parent)
  const display = buildToolDisplayFields(entry)
  const structured = buildToolStructuredFields(entry)

  return {
    id: stableEntityId(parent.id, entry, index),
    conversationId: parent.conversation_id,
    type: 'tool-call',
    content: '',
    timestamp: entry.started_at ?? parent.created_at,
    sender: 'assistant',
    participant,
    ...display,
    ...structured,
    threadId: meta.threadId,
    parentThreadId: meta.parentThreadId,
    turnStartedAt: entry.started_at ?? meta.turnStartedAt,
    agentLabel: meta.agentLabel,
    schemaVersion: optionalNumber(entry.schema_version) ?? optionalNumber(parent.schema_version),
    stepCount: parent.step_count ?? undefined,
    isPending: false,
    isStreaming: isStreamingStatus(entry.status),
    streamingContent: '',
  }
}

export const explodeToolResultsToEntities = (
  parent: MessageResponse,
  participant: ChatParticipant
): ToolCallEntity[] => {
  const results = parent.tool_results ?? []
  return results.map((entry, index) => buildEntity({ parent, participant, index, entry }))
}
